// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { createContext } from 'react';

interface TabContextValue {
  activeTab: string;
  setActiveTab: (key: string) => void;
}

const TabContext = createContext<TabContextValue | null>(null);

interface TabPanelProps {
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  children: React.ReactNode;
  'aria-label'?: string;
  variant?: 'tabs' | 'dropdown';
}

interface TabProps {
  tabKey: string;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  badge?: string | number;
}

/**
 * TabPanel component using compound components pattern
 * Manages tab state and renders tab list with active content
 */
const TabPanelComponent: React.FC<TabPanelProps> = ({
  activeTab,
  onTabChange,
  children,
  'aria-label': ariaLabel,
  variant = 'tabs',
}) => {
  const contextValue: TabContextValue = {
    activeTab,
    setActiveTab: onTabChange,
  };

  // Extract Tab children to build tab list
  const tabs = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<TabProps> =>
      React.isValidElement(child) && 
      (child.type === Tab || (child.type as any)?.__isTabComponent === true)
  );

  const activeTabContent = tabs.find(
    tab => tab.props.tabKey === activeTab
  )?.props.children;

  return (
    <TabContext.Provider value={contextValue}>
      <div className="vaccine-demo__tab-panel" aria-label={ariaLabel || "Tab panel"}>
        {variant === 'dropdown' ? (
          <div className="vaccine-demo__tab-dropdown-container">
            <label htmlFor="tab-select" className="sr-only">Select View</label>
            <select
              id="tab-select"
              className="vaccine-demo__tab-select"
              value={activeTab}
              onChange={(e) => onTabChange(e.target.value)}
              aria-label={ariaLabel || "Select View"}
            >
              {tabs.map((tab) => (
                <option 
                  key={tab.props.tabKey} 
                  value={tab.props.tabKey} 
                  disabled={tab.props.disabled}
                >
                  {tab.props.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div 
            className="vaccine-demo__tab-list"
            role="tablist"
            aria-label="Chart type selection"
          >
            {tabs.map((tab) => {
              const { tabKey, label, disabled, icon, badge } = tab.props;
              return (
                <button
                  key={tabKey}
                  onClick={() => !disabled && onTabChange(tabKey)}
                  disabled={disabled}
                  className={`vaccine-demo__tab-button ${
                    activeTab === tabKey ? 'vaccine-demo__tab-button--active' : ''
                  } ${disabled ? 'vaccine-demo__tab-button--disabled' : ''}`}
                  role="tab"
                  aria-selected={activeTab === tabKey}
                  aria-controls={`tabpanel-${tabKey}`}
                  aria-disabled={disabled}
                  id={`tab-${tabKey}`}
                >
                  {icon && <span className="tab-icon">{icon}</span>}
                  {label}
                  {badge && <span className="tab-badge">{badge}</span>}
                </button>
              );
            })}
          </div>
        )}
        
        <div
          className="vaccine-demo__tab-content"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTabContent}
        </div>
      </div>
    </TabContext.Provider>
  );
};

/**
 * Tab subcomponent - data container for tab metadata
 * Does not render itself; parent TabPanel extracts its props and children
 */
const Tab: React.FC<TabProps> = () => {
  // Tab component itself doesn't render - it's just a data container
  // The parent TabPanel extracts its props and children
  return null;
};

// Mark Tab component for identification
(Tab as any).__isTabComponent = true;

// Create TabPanel with Tab subcomponent using compound component pattern
export const TabPanel = TabPanelComponent as typeof TabPanelComponent & {
  Tab: typeof Tab;
};

TabPanel.Tab = Tab;
